import { useNavigate, useParams } from "react-router";
import {
	BottomStickyRow,
	InfoText,
	Label,
	LeadDetailsAutocompletes,
	LeadDetailsAccessDenied,
	LeadDetailsButton,
	LeadDetailsButtonRow,
	LeadDetailsContainer,
	LeadDetailsPageHeader,
	LeadDetailsScrollableContent,
	LeadDetailsScrollableContentWrapper,
	MessageContainer,
	ScrollableNonReadOnlyContent,
	StaticComponentGrid,
	StaticComponentGridDivider,
	StaticComponentTitle
} from "./LeadDetails.styles.ts";
import {
	useGetLeadAssignmentOptionsQuery,
	useGetLeadsQuery,
	useUpdateLeadAssignmentMutation
} from "features/LeadGen/api";
import * as Text from "./LeadDetails.text.ts";
import { FormHelperText, TextField } from "@mui/material";
import React, { useEffect, useMemo, useRef } from "react";
import AutoComplete from "components/Common/Autocomplete/Autocomplete.tsx";
import { LeadAssignmentPreference, LeadAssignmentStatus } from "types/declarations/enums.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import { useDialog } from "hooks/useDialog.ts";
import { getFormFieldProps } from "utils/form.tsx";
import { Controller, useFormContext } from "react-hook-form";
import { SubmitLeadFormData, submitLeadFormSchema } from "./Form/SubmitLeadFormSchema.ts";
import { useGetOrderNumbersQuery } from "features/api/orderApi.ts";
import useSnackbarEffect from "hooks/useSnackbarEffect.ts";
import Snackbar from "components/Common/SnackBar/SnackBar.tsx";
import LeadDetailsSkeleton from "./components/LeadDetailsSkeleton/LeadDetailsSkeleton.tsx";
import LeadDetailsTrackingInformationReadOnly from "./components/LeadDetailsTrackingInformationReadOnly/LeadDetailsTrackingInformationReadOnly.tsx";
import LeadDetailsInformation from "features/LeadGen/pages/LeadDetails/components/LeadDetailsInformation/LeadDetailsInformation.tsx";
import { getContactInfo } from "features/LeadGen/pages/LeadDetails/helpers/leadDetailsData.ts";
import { UnavailableDataPlaceholderText } from "constants/text.ts";
import { camelCaseToSpaced } from "features/LeadGen/pages/LeadDetails/utils/LeadDetails.utils.ts";
import DatePickerDateField from "components/DatePicker/components/DatePickerDateField.tsx";
import HelperText from "components/Form/HelperText/HelperText.tsx";
import { models } from "types/api/viewModels.ts";
import LabeledField from "features/LeadGen/pages/LeadDetails/components/LabeledField/LabeledField.tsx";
import { getAssignedToLocation } from "features/LeadGen/utils/leadAssignmentLocation.ts";
import { dateFormatTwoDigitDays } from "utils/date.ts";

const UNASSIGNED_OPTION = {
	userId: "",
	firstName: "Unassigned",
	lastName: "",
	email: ""
};

const formatPersonOptionLabel = (option?: models["LeadAssignablePersonViewModel"] | typeof UNASSIGNED_OPTION) => {
	if (!option?.userId) return "Unassigned";
	return [option.firstName, option.lastName].filter(Boolean).join(" ") || option.email || option.userId;
};

const toIsoDate = (value: Date | string | null | undefined) => {
	if (!value) return null;
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const LeadDetails = () => {
	const { leadAssignmentId } = useParams();
	const { openDialog } = useDialog();

	const { data: orderNumbers, isLoading: isOrderNumbersLoading } = useGetOrderNumbersQuery();
	const { data: leadsApiData, isLoading: isLeadsLoading } = useGetLeadsQuery();
	const {
		data: assignmentOptions,
		error: assignmentOptionsError,
		isLoading: isAssignmentOptionsLoading
	} = useGetLeadAssignmentOptionsQuery(leadAssignmentId ?? "", {
		skip: !leadAssignmentId
	});
	const [updateLeadAssignments, { isLoading: isUpdateLoading, isError: isUpdateError }] =
		useUpdateLeadAssignmentMutation();

	const { snackbarOpen } = useSnackbarEffect(isUpdateLoading);
	const navigate = useNavigate();
	const { register, handleSubmit, formState, watch, control, trigger, getValues, setError, clearErrors, setValue } =
		useFormContext();
	const formFieldData = { formState, register, schema: submitLeadFormSchema };

	const assignmentDetails = leadsApiData?.find((lead) => lead.leadAssignmentId === leadAssignmentId);
	const isForbiddenError =
		Number((assignmentOptionsError as { status?: number | string } | undefined)?.status) === 403;
	const isAssignmentMissing = !isLeadsLoading && !!leadsApiData && !assignmentDetails;
	const showAccessDenied = isForbiddenError || isAssignmentMissing;
	const lead = assignmentDetails?.lead;
	const additionalFields = lead?.additionalFields || {};
	const leadStatusTypes = Object.values(LeadAssignmentStatus);
	const cleanedStatusTypes = leadStatusTypes.filter((status) => status !== LeadAssignmentStatus.Locked);

	const containerRef = useRef<HTMLFormElement | null>(null);

	const notesValue = watch("notes") || "";
	const statusValue = watch("leadStatus");
	const assignedToUserIdValue = watch("assignedToUserId");
	const previousStatusValue = useRef<string | undefined>(undefined);
	const createdDate = dateFormatTwoDigitDays(assignmentDetails?.createdAt);
	const lastUpdatedDate = dateFormatTwoDigitDays(assignmentDetails?.updatedAt);
	const followUpMinimumDate = new Date();
	followUpMinimumDate.setHours(0, 0, 0, 0);

	const cancelLeadDetailsChange = async () => {
		navigate("/lead-assignments");
	};

	useEffect(() => {
		if (
			previousStatusValue.current === LeadAssignmentStatus.FollowUp &&
			statusValue !== LeadAssignmentStatus.FollowUp
		) {
			setValue("followUpDate", null, { shouldDirty: false, shouldValidate: true });
			clearErrors("followUpDate");
		}

		previousStatusValue.current = statusValue;
	}, [clearErrors, setValue, statusValue]);

	const onSubmit = async (data: SubmitLeadFormData) => {
		if (data.leadStatus === LeadAssignmentStatus.Declined) {
			declineLead();
			return;
		}

		try {
			await updateLeadAssignments({
				leadId: lead?.leadId ?? "",
				assignmentId: leadAssignmentId ?? "",
				request: {
					Status: data.leadStatus,
					FollowUpDate:
						data.leadStatus === LeadAssignmentStatus.FollowUp ? toIsoDate(data.followUpDate) : null,
					AssignedToUserId: data.assignedToUserId || null,
					LeadAssignmentNotes: data.notes,
					DeclineReason: data.declineReason,
					OrderNumber: data.orderNumber
				}
			}).unwrap();

			setTimeout(() => navigate("/lead-assignments"), 1000);
		} catch (error) {
			console.error(error);
		}
	};

	const handleFollowUpDateChange = (date: Date, onChange: (date: Date) => void) => {
		clearErrors("followUpDate");
		onChange(date);
	};

	const handleInvalidFollowUpDate = () => {
		setError("followUpDate", {
			type: "manual",
			message: Text.FollowUpDatePastErrorText
		});
	};

	const declineLead = () => {
		openDialog({
			dialogStatus: DialogTracking.DECLINE_LEAD,
			dialogData: {
				leadAssignmentId: leadAssignmentId,
				leadId: lead?.leadId,
				status: getValues("leadStatus"),
				notes: getValues("notes")
			}
		});
	};

	const contactInfo = useMemo(() => getContactInfo(lead), [lead]);
	const assignablePeopleOptions = useMemo(() => {
		const assignablePeople = assignmentOptions?.people ?? [];
		const people = assignmentOptions?.isAdmin
			? assignablePeople
			: assignablePeople.filter(
					(person) => person.userId?.toLowerCase() === assignmentOptions?.currentUserId?.toLowerCase()
				);
		return assignmentDetails?.assignedToUserId ? people : [UNASSIGNED_OPTION, ...people];
	}, [
		assignmentDetails?.assignedToUserId,
		assignmentOptions?.currentUserId,
		assignmentOptions?.isAdmin,
		assignmentOptions?.people
	]);
	const selectedAssignedToPerson = useMemo(
		() =>
			assignablePeopleOptions.find(
				(person) => person.userId?.toLowerCase() === (assignedToUserIdValue ?? "").toLowerCase()
			) ?? UNASSIGNED_OPTION,
		[assignablePeopleOptions, assignedToUserIdValue]
	);
	const isAssignedToReadOnly = useMemo(() => {
		if (!assignmentOptions) return true;
		if (assignmentOptions.isAdmin) return false;
		if (assignmentOptions.preference === LeadAssignmentPreference.ByAdminsOnly) return true;
		const currentUserId = assignmentOptions.currentUserId?.toLowerCase();
		const assignedToUserId = assignmentDetails?.assignedToUserId?.toLowerCase();
		return !!assignedToUserId && assignedToUserId !== currentUserId;
	}, [assignmentDetails?.assignedToUserId, assignmentOptions]);
	const isReadOnly = useMemo(
		() =>
			assignmentDetails?.status === LeadAssignmentStatus.Sold ||
			assignmentDetails?.status === LeadAssignmentStatus.Locked,
		[assignmentDetails?.status]
	);

	if (isLeadsLoading || isOrderNumbersLoading || isAssignmentOptionsLoading) {
		return <LeadDetailsSkeleton />;
	}

	if (showAccessDenied) {
		return (
			<LeadDetailsContainer>
				<LeadDetailsScrollableContent>
					<LeadDetailsPageHeader>
						<h1>{Text.LeadDetailsHeader}</h1>
					</LeadDetailsPageHeader>
					<div>
						<LeadDetailsScrollableContentWrapper>
							<LeadDetailsAccessDenied data-testid="lead-details-access-denied">
								<img
									src="/assets/NoOrdersSadBoxImage.svg"
									alt="Lead Details access denied"
									data-testid="lead-details-access-denied-img"
								/>
								<span data-testid="lead-details-access-denied-status-code">
									{Text.LeadDetailsAccessDeniedErrorCode}
								</span>
								<h2>{Text.LeadDetailsAccessDeniedHeader}</h2>
								<p>{Text.LeadDetailsAccessDeniedText}</p>
							</LeadDetailsAccessDenied>
						</LeadDetailsScrollableContentWrapper>
					</div>
				</LeadDetailsScrollableContent>
			</LeadDetailsContainer>
		);
	}

	return (
		<form
			ref={containerRef}
			id="lead-details-form"
			onSubmit={handleSubmit(onSubmit)}
		>
			<LeadDetailsContainer>
				<LeadDetailsScrollableContent>
					<LeadDetailsPageHeader>
						<h1>{Text.LeadDetailsHeader}</h1>
					</LeadDetailsPageHeader>
					<div>
						<LeadDetailsScrollableContentWrapper>
							<div>
								{isReadOnly ? (
									<LeadDetailsTrackingInformationReadOnly
										assignmentDetails={assignmentDetails}
									></LeadDetailsTrackingInformationReadOnly>
								) : (
									<ScrollableNonReadOnlyContent>
										<div>
											<StaticComponentTitle>
												{Text.TrackingInformationSubHeader}
											</StaticComponentTitle>
											<StaticComponentGrid>
												<LabeledField
													label={Text.LeadNumberLabel}
													value={assignmentDetails?.leadId}
													dataTestId="lead-details-lead-id-info"
												/>
												<LabeledField
													label={Text.LeadSourceLabel}
													value={lead?.referralSource}
													dataTestId="lead-details-lead-source-label"
												/>
												<LabeledField
													label={Text.AssignedToLocationLabel}
													value={getAssignedToLocation(assignmentDetails)}
													dataTestId="lead-details-assigned-to-info"
												/>
												<Controller
													name="assignedToUserId"
													control={control}
													render={({ field: { onChange }, fieldState: { error } }) => {
														return (
															<AutoComplete
																options={assignablePeopleOptions}
																value={selectedAssignedToPerson}
																getOptionLabel={formatPersonOptionLabel}
																isOptionEqualToValue={(option, value) =>
																	option.userId === value.userId
																}
																onChange={(_, value) => {
																	onChange(value?.userId ?? "");
																}}
																errorText={error?.message}
																isError={!!error}
																label={Text.AssignedToPersonLabel}
																dataTestId="lead-details-assigned-to-person"
																disabled={isAssignedToReadOnly}
																disableClearable
															/>
														);
													}}
												/>
												<LabeledField
													label={Text.LastUpdatedLabel}
													value={lastUpdatedDate}
													dataTestId="lead-details-last-updated-label"
												/>
												<LabeledField
													label={Text.CreatedDateLabel}
													value={createdDate}
													dataTestId="lead-details-created-date-label"
												/>
											</StaticComponentGrid>
										</div>
										<LeadDetailsAutocompletes isSold={statusValue === LeadAssignmentStatus.Sold}>
											<Controller
												name="leadStatus"
												control={control}
												render={({
													field: { onChange, value = assignmentDetails?.status ?? "" },
													fieldState: { error }
												}) => {
													return (
														<AutoComplete
															options={cleanedStatusTypes}
															value={value}
															getOptionLabel={(option) => option}
															onChange={(_, value) => {
																onChange(value);
															}}
															errorText={error?.message}
															isError={!!error}
															label={Text.LeadStatusLabel}
															dataTestId="lead-details-lead-status"
															disableClearable
														/>
													);
												}}
											/>
											{statusValue === LeadAssignmentStatus.FollowUp && (
												<Controller
													name="followUpDate"
													control={control}
													render={({
														field: {
															onChange,
															value = assignmentDetails?.followUpDate ?? null
														},
														fieldState: { error }
													}) => (
														<div>
															<DatePickerDateField
																label={Text.FollowUpDateLabel}
																value={value ? new Date(value) : null}
																firstValidDate={followUpMinimumDate}
																allowFutureDates
																allowCurrentValueOutsideSelectableDates
																restrictInputToSelectableDates
																onChange={(date) =>
																	handleFollowUpDateChange(date, onChange)
																}
																onInvalidSelectableDate={handleInvalidFollowUpDate}
																isEndIcon
																valueCanBeNull
																containerPadding={0}
															/>
															{error && (
																<FormHelperText error>
																	<HelperText
																		isError
																		text={
																			error.message ?? Text.FollowUpDateErrorText
																		}
																	/>
																</FormHelperText>
															)}
														</div>
													)}
												/>
											)}
											{statusValue === LeadAssignmentStatus.Sold && (
												<Controller
													name="orderNumber"
													control={control}
													render={({
														field: {
															onChange,
															value = assignmentDetails?.orderNumber ?? ""
														},
														fieldState: { error }
													}) => {
														return (
															<AutoComplete
																options={orderNumbers ?? []}
																value={value}
																getOptionLabel={(option) => option}
																onChange={(_, value) => {
																	onChange(value);
																}}
																errorText={Text.OrderNumberErrorText}
																isError={!!error}
																label={Text.OrderNumberLabel}
																dataTestId="lead-details-order-number"
																disableClearable
															/>
														);
													}}
												/>
											)}
											{statusValue !== LeadAssignmentStatus.FollowUp &&
												statusValue !== LeadAssignmentStatus.Sold && <div />}
										</LeadDetailsAutocompletes>
										<MessageContainer>
											<TextField
												{...getFormFieldProps({
													name: "notes",
													characterLimit: {
														currentLength: notesValue.length,
														limit: 1000
													},
													...formFieldData
												})}
												label={Text.LeadNotesLabel}
												onBlur={() => trigger("notes")}
												multiline
												rows={8}
												fullWidth
												data-testid="lead-details-notes"
											/>
										</MessageContainer>
									</ScrollableNonReadOnlyContent>
								)}

								<StaticComponentGridDivider />

								<LeadDetailsInformation
									header={Text.ContactInformationSubHeader}
									info={contactInfo}
								/>

								<StaticComponentGridDivider />

								<StaticComponentTitle>{Text.ProjectInformationSubHeader}</StaticComponentTitle>
								<StaticComponentGrid>
									{Object.entries(additionalFields)
										.filter(([key]) => key.toLowerCase() !== "metaeventid")
										.map(([key, value]) => (
											<Label key={key}>
												{camelCaseToSpaced(key)}
												<InfoText data-testid="lead-details-project-info">
													{value || UnavailableDataPlaceholderText}
												</InfoText>
											</Label>
										))}
								</StaticComponentGrid>
							</div>
						</LeadDetailsScrollableContentWrapper>
					</div>
				</LeadDetailsScrollableContent>

				<BottomStickyRow data-testid="lead-details-bottom-row">
					<LeadDetailsButtonRow>
						<LeadDetailsButton
							variant="outlined"
							color="primary"
							size="large"
							onClick={cancelLeadDetailsChange}
						>
							{Text.LeadDetailsCancel}
						</LeadDetailsButton>
						{!isReadOnly && (
							<>
								<LeadDetailsButton
									variant="contained"
									size="large"
									type="submit"
									form="lead-details-form"
								>
									{Text.LeadDetailsSave}
								</LeadDetailsButton>
								<Snackbar
									open={snackbarOpen}
									isLoading={isUpdateLoading}
									loadingText={Text.SnackBarLoadingText}
									successText={Text.SnackBarSuccessText}
									isError={isUpdateError}
									errorText={Text.SnackBarErrorText}
								/>
							</>
						)}
					</LeadDetailsButtonRow>
				</BottomStickyRow>
			</LeadDetailsContainer>
		</form>
	);
};

export default LeadDetails;
